import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Calendar, Users, Star, MapPin } from 'lucide-react';
import { useMentorship } from '../hooks/useMentorship';
import MentorCard from '../components/mentorship/MentorCard';
import SessionRequestModal from '../components/mentorship/SessionRequestModal';
import Button from '../components/ui/Button';
import type { Mentor } from '../lib/types';

const placeholderMentors: Mentor[] = [
  {
    id: '1',
    name: 'Dr. Sarah Chen',
    title: 'AI Research Lead',
    company: 'Google',
    expertise_areas: ['Machine Learning', 'Data Science', 'AI Ethics'],
    years_of_experience: 12,
    linkedin_url: 'https://linkedin.com/in/sarahchen',
    available_slots: {
      monday: ['9:00', '14:00'],
      wednesday: ['10:00', '15:00'],
      friday: ['11:00', '16:00']
    },
    bio: 'Former ML Lead at Google with PhD in Computer Science. Passionate about mentoring the next generation of AI practitioners.',
    location: 'San Francisco, CA',
    avatar_url: 'https://images.pexels.com/photos/3796217/pexels-photo-3796217.jpeg'
  },
  {
    id: '2',
    name: 'Michael Rodriguez',
    title: 'Cloud Solutions Architect',
    company: 'AWS',
    expertise_areas: ['Cloud Architecture', 'DevOps', 'System Design'],
    years_of_experience: 15,
    linkedin_url: 'https://linkedin.com/in/mrodriguez',
    available_slots: {
      tuesday: ['11:00', '16:00'],
      thursday: ['9:00', '14:00']
    },
    bio: 'Cloud Solutions Architect with extensive experience in AWS and Azure. Helping developers build scalable systems.',
    location: 'Seattle, WA',
    avatar_url: 'https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg'
  },
  {
    id: '3',
    name: 'Emily Watson',
    title: 'Senior Product Designer',
    company: 'Spotify',
    expertise_areas: ['UX Design', 'Product Strategy', 'Design Systems'],
    years_of_experience: 8,
    linkedin_url: 'https://linkedin.com/in/emilywatson',
    available_slots: {
      monday: ['10:00', '15:00'],
      wednesday: ['11:00', '16:00'],
      friday: ['9:00', '14:00']
    },
    bio: 'Senior Product Designer at Spotify. Specializing in creating intuitive user experiences and building design systems.',
    location: 'New York, NY',
    avatar_url: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg'
  },
  {
    id: '4',
    name: 'David Kim',
    title: 'Engineering Manager',
    company: 'Microsoft',
    expertise_areas: ['Engineering Leadership', 'Full Stack Development', 'Agile'],
    years_of_experience: 10,
    linkedin_url: 'https://linkedin.com/in/davidkim',
    available_slots: {
      tuesday: ['9:00', '14:00'],
      thursday: ['10:00', '15:00']
    },
    bio: 'Engineering Manager at Microsoft with a passion for building high-performing teams and mentoring future tech leaders.',
    location: 'Redmond, WA',
    avatar_url: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg'
  },
  {
    id: '5',
    name: 'Lisa Chen',
    title: 'Data Science Director',
    company: 'Netflix',
    expertise_areas: ['Data Science', 'Analytics', 'Machine Learning'],
    years_of_experience: 14,
    linkedin_url: 'https://linkedin.com/in/lisachen',
    available_slots: {
      monday: ['11:00', '16:00'],
      wednesday: ['9:00', '14:00']
    },
    bio: 'Data Science Director at Netflix, specializing in recommendation systems and personalization algorithms.',
    location: 'Los Gatos, CA',
    avatar_url: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg'
  },
  {
    id: '6',
    name: 'James Wilson',
    title: 'Security Engineer',
    company: 'Meta',
    expertise_areas: ['Cybersecurity', 'Network Security', 'Ethical Hacking'],
    years_of_experience: 12,
    linkedin_url: 'https://linkedin.com/in/jameswilson',
    available_slots: {
      tuesday: ['10:00', '15:00'],
      thursday: ['11:00', '16:00']
    },
    bio: 'Security Engineer at Meta with expertise in threat detection and prevention. Passionate about teaching cybersecurity best practices.',
    location: 'Menlo Park, CA',
    avatar_url: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg'
  }
];

const Mentorship: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const handleRequestSession = async (data: {
    mentorId: string;
    scheduledAt: string;
    notes: string;
  }) => {
    // Handle session request
    setIsModalOpen(false);
  };

  const allExpertiseAreas = Array.from(
    new Set(placeholderMentors.flatMap((mentor) => mentor.expertise_areas))
  );

  const filteredMentors = placeholderMentors.filter((mentor) => {
    const matchesSearch = 
      mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mentor.expertise_areas.some((area) =>
        area.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesExpertise =
      selectedExpertise.length === 0 ||
      mentor.expertise_areas.some((area) => selectedExpertise.includes(area));

    return matchesSearch && matchesExpertise;
  });

  const clearFilters = () => {
    setSelectedExpertise([]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Connect with Expert Mentors
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Get personalized guidance from industry professionals to accelerate your career growth.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Users className="h-5 w-5 mr-2" />
                    <span>1-on-1 Mentorship</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Star className="h-5 w-5 mr-2" />
                    <span>Expert Guidance</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <MapPin className="h-5 w-5 mr-2" />
                    <span>Global Network</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Search Input */}
                <div className="flex-1 w-full">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      placeholder="Search mentors by name or expertise..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="input pl-12 w-full bg-gray-50 border-0 focus:ring-2"
                    />
                  </div>
                </div>

                {/* Filter Button */}
                <Button
                  variant={showFilters ? 'primary' : 'outline'}
                  onClick={() => setShowFilters(!showFilters)}
                  icon={<Filter className="h-4 w-4" />}
                  className="w-full md:w-auto"
                >
                  Filters
                </Button>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-6 pt-6 border-t"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Expertise Areas</h3>
                    {selectedExpertise.length > 0 && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {allExpertiseAreas.map((area) => (
                      <button
                        key={area}
                        onClick={() =>
                          setSelectedExpertise((prev) =>
                            prev.includes(area)
                              ? prev.filter((a) => a !== area)
                              : [...prev, area]
                          )
                        }
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedExpertise.includes(area)
                            ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-500'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div variants={itemVariants} className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredMentors.length} Available Mentors
              </h2>
              <div className="text-sm text-gray-500">
                Updated daily
              </div>
            </div>
          </motion.div>

          {/* Mentor Cards Grid */}
          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredMentors.map((mentor) => (
              <MentorCard
                key={mentor.id}
                mentor={mentor}
                onRequestSession={() => {
                  setSelectedMentor(mentor);
                  setIsModalOpen(true);
                }}
              />
            ))}
          </motion.div>

          {/* Empty State */}
          {filteredMentors.length === 0 && (
            <motion.div
              variants={itemVariants}
              className="text-center py-16 bg-white rounded-2xl shadow-md"
            >
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No mentors found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters or search terms to find more mentors.
              </p>
              <Button
                variant="outline"
                onClick={clearFilters}
              >
                Clear all filters
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>

      {selectedMentor && (
        <SessionRequestModal
          mentor={selectedMentor}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedMentor(null);
          }}
          onSubmit={handleRequestSession}
        />
      )}
    </div>
  );
};

export default Mentorship;